import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from "@mui/icons-material"; // adjust icons as needed
import axios from "axios";
import { mobileFieldProps } from "./pawnCustomer/shared";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// Blue/white palette — mirrors PaymentScreen tokens
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_LIGHT = "#DBEAFE";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const SURFACE_RAISED = "#ffffff";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: SURFACE_RAISED,
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "#9CA3AF", borderWidth: "2px" },
    "&:hover fieldset": { borderColor: BLUE, borderWidth: "2.5px" },
    "&.Mui-focused fieldset": { borderColor: BLUE, borderWidth: "3px" },
  },
  "& .MuiInputLabel-root": { color: INK_MUTED, fontSize: "0.8rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE_DARK },
};

const CustomerLookupDialog = ({ open, onClose, onSelect, storeId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [filterName, setFilterName] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterFather, setFilterFather] = useState("");
  const [filterAadhar, setFilterAadhar] = useState("");
  const [filterPan, setFilterPan] = useState("");
  const [filterMode, setFilterMode] = useState("wild");

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [highlightedId, setHighlightedId] = useState(null);
  const [selectedCust, setSelectedCust] = useState(null);

  const LIMIT = 50;
  const tableRef = useRef(null);
  const filterTimerRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFilterName("");
    setFilterMobile("");
    setFilterFather("");
    setFilterAadhar("");
    setFilterPan("");
    setFilterMode("wild");
    setCustomers([]);
    setPage(1);
    setTotal(0);
    setHasMore(false);
    setHighlightedId(null);
    setSelectedCust(null);
    fetchCustomers(
      { name: "", mobile: "", father: "", aadhar: "", pan: "" },
      "wild",
      1,
      false,
    );
    setTimeout(() => nameInputRef.current?.focus(), 150);
    return () => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    };
  }, [open]);

  const fetchCustomers = async (filters, mode, pageNum, isLoadMore) => {
    if (storeId === null || storeId === undefined) return;
    const primarySearch = filters.name || filters.mobile || "";
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${storeId}`,
        {
          params: {
            search: primarySearch.trim(),
            search_mode: mode,
            sort: "ASC",
            page: pageNum,
            limit: LIMIT,
            aadhar: filters.aadhar.trim(),
            pan: filters.pan.trim(),
            relative_name: filters.father.trim(),
          },
        },
      );
      if (!res.data.success) return;
      // Server already filters all params — no client-side re-filter needed
      const data = res.data.data ?? [];
      const pagination = res.data.pagination;
      if (isLoadMore) {
        setCustomers((prev) => {
          const merged = [...prev, ...data];
          if (!highlightedId && merged.length > 0) {
            setHighlightedId(merged[0].customer_id);
            setSelectedCust(merged[0]);
          }
          return merged;
        });
      } else {
        setCustomers(data);
        if (data.length > 0) {
          setHighlightedId(data[0].customer_id);
          setSelectedCust(data[0]);
        } else {
          setHighlightedId(null);
          setSelectedCust(null);
        }
      }
      setTotal(pagination?.total ?? 0);
      setHasMore(pagination?.hasNextPage ?? false);
      setPage(pageNum);
    } catch (err) {
      console.error("Lookup fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const scheduleSearch = (overrides = {}) => {
    const filters = {
      name: filterName,
      mobile: filterMobile,
      father: filterFather,
      aadhar: filterAadhar,
      pan: filterPan,
      ...overrides,
    };
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(
      () => fetchCustomers(filters, filterMode, 1, false),
      300,
    );
  };

  const handleFilterChange = (field, value) => {
    const setters = {
      name: setFilterName,
      mobile: setFilterMobile,
      father: setFilterFather,
      aadhar: setFilterAadhar,
      pan: setFilterPan,
    };
    setters[field](value);
    scheduleSearch({ [field]: value });
  };

  const handleModeChange = (mode) => {
    setFilterMode(mode);
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    fetchCustomers(
      {
        name: filterName,
        mobile: filterMobile,
        father: filterFather,
        aadhar: filterAadhar,
        pan: filterPan,
      },
      mode,
      1,
      false,
    );
  };

  const handleScroll = () => {
    if (!tableRef.current || loading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchCustomers(
        {
          name: filterName,
          mobile: filterMobile,
          father: filterFather,
          aadhar: filterAadhar,
          pan: filterPan,
        },
        filterMode,
        page + 1,
        true,
      );
    }
  };

  const handleDialogKeyDown = (e) => {
    const tag = e.target.tagName;
    const isTyping = tag === "INPUT" || tag === "TEXTAREA";
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key === "Enter" && !isTyping) {
      e.preventDefault();
      if (selectedCust) {
        onSelect(selectedCust);
        onClose();
      }
      return;
    }
    if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !isTyping) {
      e.preventDefault();
      const idx = customers.findIndex((c) => c.customer_id === highlightedId);
      const next =
        e.key === "ArrowDown"
          ? Math.min(idx + 1, customers.length - 1)
          : Math.max(idx - 1, 0);
      setHighlightedId(customers[next].customer_id);
      setSelectedCust(customers[next]);
      const row = document.getElementById(
        `lookup-row-${customers[next].customer_id}`,
      );
      row?.scrollIntoView({ block: "nearest" });
    }
  };

  const handleRowClick = (cust) => {
    setHighlightedId(cust.customer_id);
    setSelectedCust(cust);
  };
  const handleRowDblClick = (cust) => {
    onSelect(cust);
    onClose();
  };
  const handleOK = () => {
    if (selectedCust) {
      onSelect(selectedCust);
      onClose();
    }
  };

  const COLS = [
    { key: "si", label: "#", width: 36 },
    { key: "name", label: "Name", width: 170 },
    { key: "address", label: "Address", width: 180 },
    { key: "mobile", label: "Mobile", width: 115 },
    { key: "guardian", label: "Guardian", width: 155 },
    { key: "aadhar", label: "Aadhar", width: 130 },
    { key: "pan", label: "PAN", width: 100 },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "12px",
          height: isMobile ? "100%" : "68vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: SURFACE_RAISED,
          border: `1px solid ${BORDER}`,
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        },
      }}
      onKeyDown={handleDialogKeyDown}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${BLUE_DARK} 0%, ${BLUE} 100%)`,
          color: "#fff",
          py: 1.5,
          px: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: `${BLUE}22`,
              border: `1px solid ${BLUE}44`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonIcon fontSize="small" sx={{ color: BLUE_LIGHT }} />
          </Box>
          <Typography
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: "1rem",
              color: "#fff",
            }}
          >
            Customer Selection
          </Typography>
          {total > 0 && (
            <Chip
              label={`${total} customers`}
              size="small"
              sx={{
                bgcolor: `${BLUE}22`,
                color: BLUE_LIGHT,
                fontWeight: 600,
                fontSize: 11,
                border: `1px solid ${BLUE}44`,
              }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}
        >
          ↑↓ navigate · Enter / dbl-click select · Esc close
        </Typography>
      </DialogTitle>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${BORDER}`,
          bgcolor: BLUE_PALE,
          flexShrink: 0,
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={3} md={2.5}>
            <TextField
              inputRef={nameInputRef}
              size="small"
              fullWidth
              label="Name"
              value={filterName}
              onChange={(e) => handleFilterChange("name", e.target.value)}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: BLUE }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={1.75}>
            <TextField
              size="small"
              fullWidth
              label="Mobile"
              {...mobileFieldProps()}
              value={filterMobile}
              onChange={(e) => handleFilterChange("mobile", e.target.value)}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <TextField
              size="small"
              fullWidth
              label="Guardian"
              value={filterFather}
              onChange={(e) => handleFilterChange("father", e.target.value)}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={1.75}>
            <TextField
              size="small"
              fullWidth
              label="Aadhar"
              value={filterAadhar}
              onChange={(e) => handleFilterChange("aadhar", e.target.value)}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={1.5}>
            <TextField
              size="small"
              fullWidth
              label="PAN"
              value={filterPan}
              onChange={(e) => handleFilterChange("pan", e.target.value)}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              <Typography
                variant="caption"
                sx={{ color: INK_MUTED, mr: 0.5, fontSize: 11 }}
              >
                Match:
              </Typography>
              {[
                { label: "Start", value: "start" },
                { label: "Wild", value: "wild" },
                { label: "End", value: "end" },
              ].map((m) => (
                <Chip
                  key={m.value}
                  label={m.label}
                  size="small"
                  clickable
                  onClick={() => handleModeChange(m.value)}
                  sx={{
                    fontWeight: filterMode === m.value ? 700 : 500,
                    bgcolor: filterMode === m.value ? BLUE : "transparent",
                    color: filterMode === m.value ? "#fff" : INK_MUTED,
                    border: `1px solid ${
                      filterMode === m.value ? BLUE : BORDER
                    }`,
                    fontSize: 11,
                  }}
                />
              ))}
            </Box>
          </Grid>
          {loading && (
            <Grid item xs="auto">
              <CircularProgress size={18} sx={{ color: BLUE }} />
            </Grid>
          )}
        </Grid>
      </Box>
      <DialogContent
        sx={{
          p: 0,
          overflow: "hidden",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer
          ref={tableRef}
          onScroll={handleScroll}
          sx={{ flex: 1, overflow: "auto" }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLS.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      bgcolor: INK,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 11,
                      py: 1,
                      px: 1.5,
                      width: col.width,
                      whiteSpace: "nowrap",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      borderRight: "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((cust, idx) => {
                const isHighlighted = cust.customer_id === highlightedId;
                return (
                  <TableRow
                    key={cust.customer_id}
                    id={`lookup-row-${cust.customer_id}`}
                    onClick={() => handleRowClick(cust)}
                    onDoubleClick={() => handleRowDblClick(cust)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: isHighlighted
                        ? `${BLUE}18`
                        : idx % 2 === 0
                        ? "#fff"
                        : BLUE_PALE,
                      borderLeft: isHighlighted
                        ? `3px solid ${BLUE}`
                        : "3px solid transparent",
                      "&:hover": {
                        bgcolor: isHighlighted ? `${BLUE}25` : `${BLUE}0E`,
                      },
                    }}
                  >
                    <TableCell
                      sx={{ px: 1.5, py: 0.75, color: INK_MUTED, fontSize: 11 }}
                    >
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={{ px: 1.5, py: 0.75 }}>
                      <Typography
                        variant="body2"
                        fontWeight={isHighlighted ? 700 : 500}
                        fontSize={12}
                        sx={{ color: isHighlighted ? BLUE_DARK : INK }}
                      >
                        {cust.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 1.5, py: 0.75, maxWidth: 180 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: INK_MUTED,
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cust.address || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 1.5, py: 0.75 }}>
                      <Typography
                        variant="body2"
                        fontSize={12}
                        sx={{ color: INK }}
                      >
                        {cust.mobile}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 1.5, py: 0.75 }}>
                      <Typography variant="caption" sx={{ color: INK_MUTED }}>
                        {cust.relative_relation && cust.relative_name
                          ? `${cust.relative_relation} ${cust.relative_name}`
                          : cust.relative_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 1.5, py: 0.75 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "monospace", color: INK_MUTED }}
                      >
                        {cust.aadhar || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 1.5, py: 0.75 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "monospace", color: INK_MUTED }}
                      >
                        {cust.pan || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && customers.length > 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 1.5 }}>
                    <CircularProgress size={20} sx={{ color: BLUE }} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && customers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <PersonIcon
                        sx={{ fontSize: 36, color: INK_MUTED, opacity: 0.4 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: INK_MUTED, fontSize: "0.85rem" }}
                      >
                        No customers found
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={onClose}
                        sx={{
                          borderColor: BLUE,
                          color: BLUE,
                          fontWeight: 600,
                          fontSize: "0.78rem",
                          "&:hover": { bgcolor: BLUE_PALE },
                        }}
                      >
                        + New Customer
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!hasMore && customers.length > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{
                      py: 0.75,
                      color: INK_MUTED,
                      fontSize: 11,
                      fontStyle: "italic",
                    }}
                  >
                    — end of list ({customers.length} shown) —
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${BORDER}`,
          bgcolor: BLUE_PALE,
          flexShrink: 0,
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: INK_MUTED }}>
          {selectedCust
            ? `Selected: ${selectedCust.name}  ·  ${selectedCust.mobile}`
            : "Click a row or use ↑↓ to select"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            sx={{
              borderColor: BORDER,
              color: INK_SOFT,
              "&:hover": { borderColor: BLUE, color: BLUE_DARK },
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleOK}
            disabled={!selectedCust}
            sx={{
              bgcolor: BLUE,
              color: "#fff",
              fontWeight: 700,
              "&:hover": { bgcolor: BLUE_DARK },
            }}
          >
            Select
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerLookupDialog;
